import { useEffect, useState } from 'react';

import { createLoginUrl } from '@/apiCalls/createLoginUrl';
import { DisplayComponent } from '@/components/DisplayComponent';
import { DisplayHeading } from '@/components/DisplayHeading';
import { DocumentUpload } from '@/components/DocumentUpload';
import { Header } from '@/components/Header';
import { ProgressBar } from '@/components/ProgressBar';
import {
    ELoanUsage,
    ESupportingDocCodes,
    selectFirstName,
    selectLoanUsage,
    selectSupportingDocs,
} from '@/context/borrower';
import {
    EDisplays,
    EJourneys,
    selectInitialJourney,
    setShowOcrDisplay,
} from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { selectLoginUrl, setLoginUrl } from '@/context/user';
import { useFieldCopy } from '@/copy';
import { useScreenCopy } from '@/copy';
import { getQrPushLocation } from '@/utils/getQrPushLocation';

import { IdentificationButtons } from './IdentificationButtons';
import { IdentificationSkipButton } from './IdentificationSkipButton';

export const testId = 'screen-identification';

export function Identification(): JSX.Element {
    const baseId = 'screen-identification';
    const [showErrors, setShowErrors] = useState(false);
    const dispatch = useAppDispatch();
    const screenCopy = useScreenCopy('identification');
    const fields = useFieldCopy();
    const loginUrl = useAppSelector(selectLoginUrl);
    const firstName = useAppSelector(selectFirstName);
    const initialJourney = useAppSelector(selectInitialJourney);

    // ***** Redux State *****
    const supportingDocs = useAppSelector(selectSupportingDocs);
    const frontLicense = supportingDocs?.app_drivers_licence_front
        ? supportingDocs?.app_drivers_licence_front
        : null;
    const backLicense = supportingDocs?.app_drivers_licence_back
        ? supportingDocs?.app_drivers_licence_back
        : null;

    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

    function getExpandedIndex(): number | null {
        if (!frontLicense) return 0;
        if (!backLicense) return 1;
        return null;
    }
    const loanUsage = useAppSelector(selectLoanUsage);
    const isNovatedLease = loanUsage === ELoanUsage.NOVATED;
    // Get login url for the QR code
    useEffect(() => {
        const setUrl = (url: string): void => {
            dispatch(setLoginUrl(`${url}--${EDisplays.IDENTIFICATION}`));
        };

        if (!loginUrl && !isNovatedLease) {
            createLoginUrl(getQrPushLocation(), firstName)
                .then((url) => {
                    setUrl(url);
                })
                // Without this the rejection is unhandled: the QR code is a
                // convenience, so failing to build its link should not take
                // the screen down with it.
                .catch(() => undefined);
        }

        const [url, display] = loginUrl.split('--');
        if (display !== EDisplays.IDENTIFICATION) {
            setUrl(url);
        }
    }, []);

    // Probably will work??
    useEffect(() => {
        const number = getExpandedIndex();
        setExpandedIndex(number);
        if (frontLicense) {
            setExpandedIndex(1);
        }
        setShowOcrDisplay(true);
    }, [supportingDocs]);

    function documentUpload(index: number): void {
        if (expandedIndex === index) {
            setExpandedIndex(null);
            return;
        }
        setExpandedIndex(index);
    }

    // ***** Render *****
    return (
        <div data-testid={testId}>
            <DisplayComponent baseId={baseId}>
                <Header />
                <ProgressBar />

                <DisplayHeading
                    title={screenCopy.title}
                    infoText={screenCopy.subtitle}
                />
                {/* TODO: Document Uploads below should be user input components for driverslicensefront and driverslicenseback - They are the same for supporting docs */}
                <DocumentUpload
                    baseId={baseId}
                    showErrors={
                        showErrors &&
                        (!supportingDocs[
                            ESupportingDocCodes.DRIVERS_LICENSE_FRONT
                        ] ||
                            (supportingDocs[
                                ESupportingDocCodes.DRIVERS_LICENSE_FRONT
                            ] &&
                                supportingDocs[
                                    ESupportingDocCodes.DRIVERS_LICENSE_FRONT
                                ].length === 0))
                    }
                    code={ESupportingDocCodes.DRIVERS_LICENSE_FRONT}
                    document={frontLicense}
                    value={fields.licenceFront}
                    expanded={expandedIndex === 0}
                    handleExpanded={(): void => documentUpload(0)}
                />
                <DocumentUpload
                    baseId={baseId}
                    showErrors={
                        showErrors &&
                        (!supportingDocs[
                            ESupportingDocCodes.DRIVERS_LICENSE_BACK
                        ] ||
                            (supportingDocs[
                                ESupportingDocCodes.DRIVERS_LICENSE_BACK
                            ] &&
                                supportingDocs[
                                    ESupportingDocCodes.DRIVERS_LICENSE_BACK
                                ].length === 0))
                    }
                    code={ESupportingDocCodes.DRIVERS_LICENSE_BACK}
                    document={backLicense}
                    value={fields.licenceBack}
                    expanded={expandedIndex === 1}
                    handleExpanded={(): void => documentUpload(1)}
                />

                {initialJourney !== EJourneys.TEST_DRIVE && (
                    <IdentificationSkipButton baseId={baseId} />
                )}
                <IdentificationButtons
                    baseId={baseId}
                    setShowErrors={setShowErrors}
                />
            </DisplayComponent>
        </div>
    );
}
